import React, { ReactElement } from 'react'
import ReactDOM from 'react-dom'
import mapboxgl from 'mapbox-gl'

import { GeoPoint } from '~types'

// Style wrapper component to ensure consistent styling
const PopupContentWrapper = ({ children }: { children: React.ReactNode }): ReactElement => {
  return (
    <div className='map-popup-content'>
      <style>
        {`
          .map-popup-content {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 12px;
            min-width: 200px;
          }

          .map-popup-content h1,
          .map-popup-content h2,
          .map-popup-content h3 {
            margin: 0 0 8px 0;
            font-weight: 600;
            color: #1a1a1a;
          }

          .map-popup-content p {
            margin: 0 0 8px 0;
            color: #4a4a4a;
            line-height: 1.4;
          }

          .map-popup-content button {
            background-color:rgb(0, 0, 0);
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            transition: background-color 0.2s;
          }

          .map-popup-content button:hover {
            background-color:rgb(0, 0, 0);
          }

          /* Override default Mapbox popup styles */
          .mapboxgl-popup-content {
            padding: 0;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          }

          .mapboxgl-popup-close-button {
            padding: 4px 8px;
            font-size: 16px;
            color: #666;
            right: 4px;
            top: 4px;
          }

          .mapboxgl-popup-close-button:hover {
            background: none;
            color: #333;
          }

          /* Scrollable list styles */
          .map-popup-scrollable-list {
            max-height: 300px;
            overflow-y: auto;
            padding: 0;
          }

          .map-popup-case-item {
            padding: 8px 0;
            border-bottom: 1px solid #eee;
          }

          .map-popup-case-item:last-child {
            border-bottom: none;
          }
        `}
      </style>
      {children}
    </div>
  )
}

const ScrollableList = ({ cases }: { cases: ExtendedGeoPoint[] }): ReactElement => {
  return (
    <div className='map-popup-scrollable-list'>
      {cases.map((caseItem, index) => (
        <div key={index} className='map-popup-case-item'>
          {caseItem.popupContent}
        </div>
      ))}
    </div>
  )
}

export interface ExtendedGeoPoint extends GeoPoint {
  popupContent?: React.ReactNode | null
  caseCount?: number
  customData?: Record<string, number>
}

export const showMultiCasePopup = (
  map: mapboxgl.Map,
  cases: ExtendedGeoPoint[],
  coordinates: [number, number],
): void => {
  if (cases.length === 0) return

  const popupNode = document.createElement('div')

  // Create a wrapper component to handle cleanup and styling
  const PopupWrapper = (): ReactElement => {
    React.useEffect(() => {
      return (): void => {
        ReactDOM.unmountComponentAtNode(popupNode)
      }
    }, [])

    return (
      <PopupContentWrapper>
        <ScrollableList cases={cases} />
      </PopupContentWrapper>
    )
  }

  ReactDOM.render(<PopupWrapper />, popupNode)

  // Remove existing popups
  const existingPopups = document.getElementsByClassName('mapboxgl-popup')
  Array.from(existingPopups).forEach((popup) => popup.remove())

  // Create and add the new popup
  new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: false,
    maxWidth: '500px',
    className: 'custom-popup cases-popup',
    offset: [0, -10],
  })
    .setLngLat(coordinates)
    .setDOMContent(popupNode)
    .addTo(map)
}

export const removeClusteredPointsFromMap = (
  map: mapboxgl.Map,
  sourceId: string = 'clustered-points',
): void => {
  try {
    if (map.getLayer(`${sourceId}-clusters`)) {
      map.removeLayer(`${sourceId}-clusters`)
    }
    if (map.getLayer(`${sourceId}-cluster-count`)) {
      map.removeLayer(`${sourceId}-cluster-count`)
    }
    if (map.getLayer(`${sourceId}-unclustered-point`)) {
      map.removeLayer(`${sourceId}-unclustered-point`)
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId)
    }
  } catch (error) {
    console.error('Error removing points:', error)
  }
}

export const updateClusteredPointsOnMap = (
  map: mapboxgl.Map,
  points: ExtendedGeoPoint[],
  fitMapToPoints: boolean = true,
  sourceId: string = 'clustered-points',
): void => {
  try {
    // Remove existing points first
    removeClusteredPointsFromMap(map, sourceId)

    // Add new source
    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: points.map((point, index) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.lon, point.lat],
          },
          properties: {
            id: index,
            case_count: point.caseCount || 1,
            ...point.customData,
          },
        })),
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      clusterProperties: {
        // Sum up the case_count properties for each point in the cluster
        sum_case_count: ['+', ['get', 'case_count']],
      },
    })

    // Add clusters layer
    map.addLayer({
      id: `${sourceId}-clusters`,
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'point_count'],
          10,
          'rgba(0, 255, 255, 0.7)',
          50,
          'rgba(0, 255, 0, 0.7)',
          250,
          'rgba(255, 255, 0, 0.7)',
        ],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['get', 'point_count'],
          10,
          15,
          50,
          25,
          250,
          35,
        ],
        'circle-stroke-width': 3,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.5)',
      },
    })

    // Calculate total case count for clusters
    map.addLayer({
      id: `${sourceId}-cluster-count`,
      type: 'symbol',
      source: sourceId,
      filter: ['has', 'point_count'],
      layout: {
        // Display the sum of case counts
        // We're using point_count as a fallback, but our implementation uses properties.case_count
        'text-field': '{sum_case_count}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': 'black',
      },
    })

    // Add unclustered point layer
    map.addLayer({
      id: `${sourceId}-unclustered-point`,
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': 'rgba(0, 255, 255, 0.7)',
        'circle-radius': 5,
        'circle-stroke-width': 1,
        'circle-stroke-color': 'white',
      },
    })

    // Add popups for unclustered points
    map.on('click', `${sourceId}-unclustered-point`, (e) => {
      if (!e.features || e.features.length === 0) return

      const feature = e.features[0]
      if (feature.geometry.type !== 'Point') return

      const coordinates = feature.geometry.coordinates.slice()
      const pointIndex = feature.properties?.id
      const point = points[pointIndex]

      if (point?.popupContent) {
        const popupNode = document.createElement('div')

        // Create a wrapper component to handle cleanup and styling
        const PopupWrapper = (): ReactElement => {
          React.useEffect(() => {
            return (): void => {
              ReactDOM.unmountComponentAtNode(popupNode)
            }
          }, [])

          return <PopupContentWrapper>{point.popupContent}</PopupContentWrapper>
        }

        ReactDOM.render(<PopupWrapper />, popupNode)

        // Remove existing popups
        const existingPopups = document.getElementsByClassName('mapboxgl-popup')
        Array.from(existingPopups).forEach((popup) => popup.remove())

        // Create and add the new popup
        new mapboxgl.Popup({
          closeButton: true,
          closeOnClick: false,
          maxWidth: '400px',
          className: 'custom-popup',
          offset: [0, -10],
        })
          .setLngLat(coordinates as [number, number])
          .setDOMContent(popupNode)
          .addTo(map)
      }
    })

    map.on('click', `${sourceId}-clusters`, (e) => {
      if (!e.features || e.features.length === 0) return

      const feature = e.features[0]
      if (!feature.properties || !feature.geometry || feature.geometry.type !== 'Point') return

      const clusterId = feature.properties.cluster_id
      const pointGeometry = feature.geometry as GeoJSON.Point
      const coordinates = pointGeometry.coordinates.slice() as [number, number]

      const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
      source.getClusterLeaves(
        clusterId,
        100, // Limit to 100 cases
        0, // Offset
        (err, clusterFeatures) => {
          if (err) {
            console.error('Error getting cluster leaves:', err)
            return
          }

          if (!clusterFeatures) return

          const clusterPoints = clusterFeatures
            .map((feature) => {
              if (!feature.properties) return null
              const pointId = feature.properties.id
              return points[pointId]
            })
            .filter(
              (point): point is ExtendedGeoPoint =>
                point !== null &&
                point !== undefined &&
                point.popupContent !== undefined &&
                point.popupContent !== null,
            )

          showMultiCasePopup(map, clusterPoints, coordinates)
        },
      )
    })

    // Fit bounds if needed
    if (fitMapToPoints && points.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      points.forEach((point) => bounds.extend([point.lon, point.lat]))
      map.fitBounds(bounds, { padding: 50 })
    }
  } catch (error) {
    console.error('Error updating points:', error)
  }
}
